"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useDebounceValue } from "usehooks-ts";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FilterButton,
  type ActiveFilter,
  type FilterFieldDef,
  type SearchableFilterOption,
} from "@/components/ui/filter-panel";
import { Icon } from "@/components/ui/icon";
import { EmptySequencesIllustration } from "@/components/illustrations/empty-sequences-illustration";
import { Pagination } from "@/components/ui/pagination";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Toolbar } from "@/components/ui/toolbar";
import { useAuth } from "@/features/auth";
import { CreateSequenceJobPicker } from "@/features/sequences/components/create-sequence-job-picker";
import {
  KPI_COLUMNS,
  type KpiColumn,
} from "@/features/sequences/components/list/table";
import { useProjectSequenceFilterJobs } from "@/features/sequences/hooks/use-project-sequence-filter-jobs";
import {
  useProjectSequencesByJob,
  type SequenceJobGroup,
} from "@/features/sequences/hooks/use-project-sequences-by-job";
import type { SequenceListItem } from "@/features/sequences/hooks/use-sequences";
import { activeFiltersToByJobParams } from "@/features/sequences/lib/by-job-query-params";
import { useProjectSequencesSheet } from "@/hooks/use-project-sequences-sheet";
import { formatRelativeTime } from "@/lib/date/format-relative-time";
import { cn } from "@/lib/utils";

const PER_PAGE = 10;
const SEARCH_DEBOUNCE_MS = 300;

function senderInitials(name: string): string {
  const parts = name
    .split(/\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return (
    (parts[0][0] ?? "") + (parts[parts.length - 1][0] ?? "")
  ).toUpperCase();
}

function SequenceRowSkeletons({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-6" aria-hidden>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2.5">
          <div className="flex items-center justify-between px-1">
            <div className="h-4 w-44 rounded bg-contrast/8 shimmer shimmer-bg text-foreground" />
            <div className="h-3 w-20 rounded bg-contrast/6" />
          </div>
          <div className="divide-y divide-border overflow-hidden rounded-xl border border-border">
            {Array.from({ length: 2 }).map((_, j) => (
              <div key={j} className="flex items-center gap-4 px-4 py-4">
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="h-3.5 w-1/2 rounded bg-contrast/8" />
                  <div className="h-3 w-1/3 rounded bg-contrast/5" />
                </div>
                <div className="hidden gap-8 sm:flex">
                  {Array.from({ length: 4 }).map((_, k) => (
                    <div key={k} className="flex flex-col items-end gap-1.5">
                      <div className="h-4 w-10 rounded bg-contrast/8" />
                      <div className="h-2.5 w-14 rounded bg-contrast/5" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Headline stats ───────────────────────────────────────────────────── */

/** `part` as a whole-number percentage of `total`; "0%" when nothing sent. */
function percent(part: number, total: number): string {
  if (total <= 0) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

/** Same glyphs and hues as the sequence table's KPI columns, for continuity. */
const STAT_ICONS = Object.fromEntries(
  KPI_COLUMNS.map((col) => [col.key, col]),
) as Record<KpiColumn["key"], KpiColumn>;

function Stat({
  metric,
  value,
  label,
  emphasis = "default",
}: {
  metric: KpiColumn["key"];
  value: string;
  label: string;
  /** `ai` tints the value in brand gold — reserved for the metric that pays. */
  emphasis?: "default" | "ai";
}) {
  const { icon, iconColor } = STAT_ICONS[metric];
  return (
    <div className="flex min-w-[3.25rem] flex-col items-end gap-0.5">
      <span
        className={cn(
          "text-[15px] font-semibold leading-5 tabular-nums tracking-[-0.01em]",
          emphasis === "ai" ? "text-[#c2710a] dark:text-ai" : "text-heading",
        )}
      >
        {value}
      </span>
      <span className="flex items-center gap-1 text-[11px] leading-4 text-contrast/45">
        <Icon
          name={icon}
          size={11}
          aria-hidden
          className={cn("shrink-0 opacity-80", iconColor)}
        />
        {label}
      </span>
    </div>
  );
}

/**
 * Four numbers a recruiter actually asks about, in funnel order. Rates where
 * a rate is the question (did they open, did they reply), counts where the
 * count is (how many are in, how many are warm). The full eight-column
 * breakdown lives on the sequence page itself.
 */
function HeadlineStats({
  analytics,
  needsSetup,
}: {
  analytics: SequenceListItem["analytics"];
  needsSetup: boolean;
}) {
  const total = analytics?.total ?? 0;

  if (total === 0) {
    return needsSetup ? (
      <p className="flex items-center gap-1.5 text-[13px] leading-5 text-[#b45309] dark:text-[#FFD083] sm:text-right">
        <Icon name="IconWarningSign" size={13} aria-hidden className="shrink-0" />
        Finish setup to start enrolling
      </p>
    ) : (
      <p className="text-[13px] leading-5 text-contrast/40 sm:text-right">
        No enrollments yet
      </p>
    );
  }

  const active = analytics.in_progress ?? 0;
  return (
    <dl className="flex shrink-0 items-start gap-4">
      <Stat
        metric={active > 0 ? "in_progress" : "total"}
        value={total.toLocaleString()}
        label={active > 0 ? `${active.toLocaleString()} active` : "enrolled"}
      />
      <Stat
        metric="opened"
        value={percent(analytics.opened, total)}
        label="opened"
      />
      <Stat
        metric="replied"
        value={percent(analytics.replied, total)}
        label="replied"
      />
      <Stat
        metric="interested"
        value={(analytics.interested ?? 0).toLocaleString()}
        label="interested"
        emphasis={analytics.interested > 0 ? "ai" : "default"}
      />
    </dl>
  );
}

/* ── Rows & sections ──────────────────────────────────────────────────── */

function SequenceSheetRow({ seq }: { seq: SequenceListItem }) {
  const senderName = seq.sender?.name ?? "Unknown sender";
  const stepCount = seq.step_count ?? 0;
  const stepLabel = `${stepCount} ${stepCount === 1 ? "step" : "steps"}`;
  const href = `/jobs/${seq.job_id}/sequences/${seq.id}/steps`;
  const needsSetup =
    seq.enrollment_blocked_reason === "unconfigured_steps" ||
    (seq.unconfigured_step_count ?? 0) > 0;
  const timeAgo = formatRelativeTime(seq.updated_at);

  return (
    <Link
      href={href}
      className={cn(
        "group/row relative flex flex-col gap-3 px-4 py-3.5 transition-colors duration-150",
        "sm:flex-row sm:items-center sm:gap-5",
        "outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ai/40",
        needsSetup && !seq.is_archived
          ? // Amber wash: it can't enroll anyone until someone finishes it,
            // so it should read as a to-do, not just another row.
            "bg-[rgba(255,175,83,0.06)] hover:bg-[rgba(255,175,83,0.1)] focus-visible:bg-[rgba(255,175,83,0.1)]"
          : "hover:bg-contrast/[0.035] focus-visible:bg-contrast/[0.035]",
        seq.is_archived && "opacity-60 hover:opacity-100",
      )}
    >
      <div className="flex min-w-0 flex-1 items-center">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-[15px] font-medium leading-5 tracking-[-0.01em] text-heading">
              {seq.name}
            </span>
            {seq.is_archived ? (
              <Badge variant="muted" size="sm" className="shrink-0">
                Archived
              </Badge>
            ) : needsSetup ? (
              <Badge variant="warning" size="sm" className="shrink-0">
                Needs setup
              </Badge>
            ) : null}
          </div>
          {/* The sender never truncates — the title above does, and the
              steps / time run onto a second line if the row gets tight. */}
          <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[13px] leading-4 text-contrast/50">
            <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
              <Avatar className="size-4 shrink-0 shadow-[inset_0_0_0_1px_var(--border-alpha)]">
                <AvatarImage src={seq.sender?.avatar_url ?? undefined} alt="" />
                <AvatarFallback className="bg-contrast/8 text-[8px] font-semibold text-contrast/70">
                  {senderInitials(senderName)}
                </AvatarFallback>
              </Avatar>
              {senderName}
            </span>
            <span aria-hidden className="shrink-0 text-contrast/25">
              ·
            </span>
            <span className="shrink-0 whitespace-nowrap">{stepLabel}</span>
            <span aria-hidden className="shrink-0 text-contrast/25">
              ·
            </span>
            <span className="shrink-0 whitespace-nowrap">{timeAgo}</span>
          </p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3">
        <HeadlineStats
          analytics={seq.analytics}
          needsSetup={needsSetup && !seq.is_archived}
        />
        <Icon
          name="IconChevronRight"
          size={16}
          aria-hidden
          className="hidden shrink-0 text-contrast/25 transition-[color,transform] duration-150 group-hover/row:translate-x-0.5 group-hover/row:text-contrast/60 sm:block"
        />
      </div>
    </Link>
  );
}

function JobSection({ group }: { group: SequenceJobGroup }) {
  const timeAgo = formatRelativeTime(group.latest_sequence_updated_at);
  const countLabel = `${group.sequence_count} ${
    group.sequence_count === 1 ? "sequence" : "sequences"
  }`;

  return (
    <section
      aria-label={group.job_name}
      className="flex flex-col gap-2.5"
    >
      <header className="flex items-baseline justify-between gap-3 px-1">
        <h3 className="flex min-w-0 items-baseline gap-2">
          <span className="truncate text-[15px] font-semibold leading-5 tracking-[-0.01em] text-heading">
            {group.job_name}
          </span>
          <span className="shrink-0 text-[13px] leading-5 tabular-nums text-contrast/45">
            {countLabel}
          </span>
        </h3>
        <span className="shrink-0 text-xs leading-4 text-contrast/40">
          Updated {timeAgo}
        </span>
      </header>

      <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-surface-primary dark:bg-contrast/[0.02]">
        {group.sequences.map((seq) => (
          <SequenceSheetRow key={seq.id} seq={seq} />
        ))}
      </div>
    </section>
  );
}

export function ProjectSequencesSheet() {
  const { selectedProjectId } = useAuth();
  const { projectSequencesOpen, closeProjectSequences } =
    useProjectSequencesSheet();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, SEARCH_DEBOUNCE_MS);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);

  const byJobParams = useMemo(
    () => activeFiltersToByJobParams(debouncedSearch, activeFilters),
    [debouncedSearch, activeFilters],
  );

  const hasActiveQuery = Object.keys(byJobParams).length > 0;

  const {
    jobs,
    totalSequences,
    isPending,
    isFetching,
    isPlaceholderData,
    isError,
    refetch,
  } = useProjectSequencesByJob({
    projectId:
      projectSequencesOpen && selectedProjectId ? selectedProjectId : null,
    enabled: projectSequencesOpen,
    page,
    perPage: PER_PAGE,
    params: byJobParams,
  });

  const { jobs: filterJobs } = useProjectSequenceFilterJobs({
    projectId:
      projectSequencesOpen && selectedProjectId ? selectedProjectId : null,
    enabled: projectSequencesOpen,
  });

  const filterFields = useMemo<FilterFieldDef[]>(() => {
    const searchableOptions: SearchableFilterOption[] = filterJobs.map(
      (job) => ({
        id: job.job_id,
        label: job.job_name,
      }),
    );
    return [
      {
        key: "job",
        label: "Job",
        icon: "IconBag",
        type: "searchable",
        options: [],
        searchableOptions,
        placeholder: "Select job…",
      },
      {
        key: "steps",
        label: "Steps",
        icon: "IconAgent",
        type: "number",
        options: [],
        placeholder: "0",
      },
      {
        key: "enrollments",
        label: "Enrollments",
        icon: "IconPaperPlane",
        type: "number",
        options: [],
        placeholder: "0",
      },
    ];
  }, [filterJobs]);

  const clearFilters = () => {
    setSearch("");
    setActiveFilters([]);
    setFilterOpen(false);
    setPage(1);
  };

  useEffect(() => {
    if (!projectSequencesOpen) {
      setSearch("");
      setActiveFilters([]);
      setFilterOpen(false);
      setPage(1);
    }
  }, [projectSequencesOpen]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilters]);

  const totalPages = Math.max(1, Math.ceil(totalSequences / PER_PAGE));

  // Dataset can shrink while the user is still on a later page (archive,
  // filter change race, etc.). Snap back so we don't render a false empty
  // with the footer still showing "of N".
  useEffect(() => {
    if (isPending || (isFetching && isPlaceholderData)) return;
    if (page > totalPages) setPage(totalPages);
  }, [isPending, isFetching, isPlaceholderData, page, totalPages]);

  if (!selectedProjectId) return null;

  const showSkeletons =
    isPending ||
    (isFetching && isPlaceholderData) ||
    // Stale later page after the dataset shrinks: avoid a blank body
    // while the page clamp effect snaps back.
    (!isError && jobs.length === 0 && totalSequences > 0);
  const showError = isError && !showSkeletons;
  const showEmptyFiltered =
    !showSkeletons &&
    !showError &&
    totalSequences === 0 &&
    jobs.length === 0 &&
    hasActiveQuery;
  const showEmptyProject =
    !showSkeletons &&
    !showError &&
    totalSequences === 0 &&
    jobs.length === 0 &&
    !hasActiveQuery;

  const rangeStart = (page - 1) * PER_PAGE + 1;
  const rangeEnd = Math.min(page * PER_PAGE, totalSequences);

  return (
    <Sheet
      open={projectSequencesOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) closeProjectSequences();
      }}
      size="xl"
      disableFocusTrap
    >
      <SheetHeader>
        <div className="flex items-center gap-1.5">
          <Icon name="IconPaperPlane" size={18} className="text-heading" />
          <SheetTitle className="inline-flex items-center gap-1.5">
            Sequences in This Project
            {totalSequences > 0 && (
              <span className="text-lg font-medium text-contrast/50">
                {totalSequences}
              </span>
            )}
          </SheetTitle>
        </div>
        <SheetClose />
      </SheetHeader>

      <Toolbar.Container
        role="search"
        aria-label="Filter sequences"
        className="shrink-0 px-6"
      >
        <Toolbar.Search
          value={search}
          onChange={setSearch}
          placeholder="Search jobs, sequences or senders"
          aria-label="Search by job, sequence, or sender"
          className="min-w-0 flex-1 sm:w-[240px] sm:flex-none"
        />
        <Toolbar.Separator />
        <FilterButton
          fields={filterFields}
          filters={activeFilters}
          onFiltersChange={setActiveFilters}
          open={filterOpen}
          onOpenChange={setFilterOpen}
        />
        <div className="ml-auto flex shrink-0 items-center">
          <CreateSequenceJobPicker align="end" size="xs" />
        </div>
      </Toolbar.Container>

      <SheetBody
        rootClassName="bg-background dark:bg-surface-elevated"
        contentClassName={
          showEmptyProject || showEmptyFiltered || showError
            ? "flex min-h-full flex-col"
            : undefined
        }
        className={cn(
          "gap-6 py-6",
          (showEmptyProject || showEmptyFiltered || showError) &&
            "min-h-full flex-1 justify-center",
        )}
      >
        {showSkeletons ? (
          <div aria-busy>
            <SequenceRowSkeletons count={2} />
          </div>
        ) : showError ? (
          <div className="flex flex-col items-center justify-center gap-3 px-10 py-16 text-center">
            <p className="text-base font-medium text-heading">
              Couldn&apos;t load sequences
            </p>
            <p className="max-w-sm text-sm text-contrast/60">
              Something went wrong fetching sequences for this project.
            </p>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                void refetch();
              }}
            >
              Try again
            </Button>
          </div>
        ) : showEmptyProject ? (
          <div className="flex flex-col items-center justify-center px-10 py-16">
            <div className="flex flex-col items-center gap-4">
              <EmptySequencesIllustration draggable={false} />
              <div className="flex flex-col items-center gap-[7px] text-center">
                <h3 className="text-xl font-medium tracking-[-0.2px] text-heading">
                  No sequences yet
                </h3>
                <p className="text-sm tracking-[-0.14px] text-contrast/60">
                  Pick a job to create your first outreach sequence.
                </p>
              </div>
              <CreateSequenceJobPicker />
            </div>
          </div>
        ) : showEmptyFiltered ? (
          <div className="flex flex-col items-center justify-center px-10 py-16">
            <div className="flex flex-col items-center gap-4">
              <EmptySequencesIllustration draggable={false} />
              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col items-center gap-[7px] text-center">
                  <h3 className="text-xl font-medium tracking-[-0.2px] text-heading">
                    No matching sequences
                  </h3>
                  <p className="text-sm tracking-[-0.14px] text-contrast/60">
                    Try a different search or clear your filters.
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={clearFilters}
                  variant="secondary"
                  size="sm"
                  className="bg-contrast text-background hover:bg-contrast/90"
                >
                  Clear filters
                  <Icon name="IconCrossSmall" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-7">
            {jobs.map((group) => (
              <JobSection key={group.job_id} group={group} />
            ))}
          </div>
        )}
      </SheetBody>

      {totalSequences > PER_PAGE && !showError && (
        <Pagination.Root
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          variant="sheet"
        >
          <div className="bg-contrast/5 dark:bg-background/10 border-t border-border flex items-center justify-between gap-3 px-3 h-12 shrink-0">
            <span className="text-sm font-medium text-contrast/70 whitespace-nowrap shrink-0">
              {rangeStart} - {rangeEnd} of {totalSequences} Sequences
            </span>
            <Pagination.Row>
              <Pagination.Prev />
              <Pagination.Pages
                strategy="window"
                maxVisible={4}
                windowAlign="center"
              />
              <Pagination.Next />
            </Pagination.Row>
          </div>
        </Pagination.Root>
      )}
    </Sheet>
  );
}
